import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';

@Component({
    selector: 'app-timer',
    templateUrl: './timer.component.html',
    styleUrls: ['./timer.component.scss'],
    standalone: true,
})
export class TimerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() timerDuration: number = 60000;
    @Input() showMilliseconds: boolean = false;
    @Input() showSeconds: boolean = true;
    @Input() showMinutes: boolean = true;
    @Input() showHours: boolean = false;
    @Input() autoStart: boolean = true;

    @Output() timerComplete: EventEmitter<void> = new EventEmitter<void>();

    public remainingTime: number = 0;
    public displayTime = {
        hours: '00',
        minutes: '00',
        seconds: '00',
        milliseconds: '000',
    };

    private timerSubscription: Subscription | null = null;
    private isPaused: boolean = false;
    private isStarted: boolean = false; // Tracks if the timer has been started

    public ngOnInit() {
        this.reset();

        if (this.autoStart) {
            this.start();
        }
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['timerDuration'] && !changes['timerDuration'].firstChange) {
            this.reset();
        }
    }

    public ngOnDestroy() {
        this.clearTimer();
    }

    public start() {
        if (!this.isStarted) {
            this.isPaused = false;
            this.isStarted = true;
            this.startTimer();
        }
    }

    private startTimer() {
        const intervalTime = this.showMilliseconds ? 10 : 1000; // Update every 10ms or 1s
        this.timerSubscription = interval(intervalTime).subscribe(() => {
            if (!this.isPaused) {
                this.remainingTime -= intervalTime;

                if (this.remainingTime <= 0) {
                    this.remainingTime = 0;
                    this.updateDisplayTime();
                    this.clearTimer();
                    this.timerComplete.emit();
                } else {
                    this.updateDisplayTime();
                }
            }
        });
    }

    public pause() {
        this.isPaused = true;
    }

    public resume() {
        if (this.isPaused) {
            this.isPaused = false;
        }
    }

    public reset() {
        this.isPaused = false;
        this.isStarted = false;
        this.remainingTime = this.timerDuration;
        this.updateDisplayTime();
        this.clearTimer();

        if (this.autoStart) {
            this.start();
        }
    }

    private updateDisplayTime() {
        let tempTime = this.remainingTime;

        const hours = Math.floor(tempTime / 3600000);
        tempTime %= 3600000;

        const minutes = Math.floor(tempTime / 60000);
        tempTime %= 60000;

        const seconds = Math.floor(tempTime / 1000);
        tempTime %= 1000;

        const milliseconds = tempTime;

        this.displayTime.hours = this.pad(hours, 2);
        this.displayTime.minutes = this.pad(minutes, 2);
        this.displayTime.seconds = this.pad(seconds, 2);
        this.displayTime.milliseconds = this.pad(milliseconds, 3);
    }

    private pad(num: number, size: number): string {
        return num.toString().padStart(size, '0');
    }

    private clearTimer() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }
}
